// Server clock estimate for cast sync. Spec: docs/superpowers/specs/2026-10-12-cast-sync-protocol-design.md §2.
import { getCastServerTime } from "../../api/stream";
import logger from "../logger";

export const CLOCK_SAMPLES = 5;
export const CLOCK_REFRESH_MS = 300_000;
export const CLOCK_REQUEST_TIMEOUT_MS = 3000;
/** An offset change smaller than this is jitter: subscribers (which resync players) are not told. */
export const CLOCK_NOTIFY_MS = 50;
const JUMP_WARN_MS = 200;

export interface ClockSample {
  offset: number;
  rtt: number;
}

/** offset = server time − the request's midpoint on the monotonic clock. */
export function sampleOf(sentAt: number, serverTime: number, receivedAt: number): ClockSample {
  return { offset: serverTime - (sentAt + receivedAt) / 2, rtt: receivedAt - sentAt };
}

export function bestSample(samples: ClockSample[]): ClockSample | null {
  let best: ClockSample | null = null;
  for (const s of samples) if (!best || s.rtt < best.rtt) best = s;
  return best;
}

/** Server epoch ms = monotonic + offset; the device clock until anything better is known. */
export class ServerClock {
  private offset: number | null = null;
  private exact = false;
  private running: Promise<void> | null = null;
  private readonly listeners = new Set<() => void>();

  constructor(
    private readonly fetchServerTime: () => Promise<number>,
    private readonly mono: () => number = () => performance.now(),
    private readonly wall: () => number = () => Date.now(),
  ) {}

  now(): number {
    const m = this.mono();
    return m + (this.offset ?? this.wall() - m);
  }

  monoNow(): number {
    return this.mono();
  }

  hasSample(): boolean {
    return this.exact;
  }

  /** Called when the estimate moves by CLOCK_NOTIFY_MS or more (first real sample, refresh jump). */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private setOffset(next: number): void {
    const m = this.mono();
    const before = this.offset ?? this.wall() - m;
    this.offset = next;
    if (Math.abs(next - before) >= CLOCK_NOTIFY_MS) {
      for (const l of [...this.listeners]) l();
    }
  }

  /** A RabbitMQ event's serverTime (delivery latency included): used only until a real sample exists. */
  noteServerTime(serverTime: number): void {
    if (this.exact || !Number.isFinite(serverTime)) return;
    this.setOffset(serverTime - this.mono());
  }

  sync(samples: number = CLOCK_SAMPLES): Promise<void> {
    if (!this.running) {
      this.running = this.measure(samples).finally(() => {
        this.running = null;
      });
    }
    return this.running;
  }

  private async measure(samples: number): Promise<void> {
    const got: ClockSample[] = [];
    for (let i = 0; i < samples; i++) {
      const sentAt = this.mono();
      try {
        const serverTime = await this.withTimeout(this.fetchServerTime());
        if (typeof serverTime !== "number" || !Number.isFinite(serverTime)) continue;
        got.push(sampleOf(sentAt, serverTime, this.mono()));
      } catch {
        // one lost sample is fine; all lost keeps the previous offset
      }
    }
    const best = bestSample(got);
    if (!best) return;
    if (this.exact && this.offset != null && Math.abs(best.offset - this.offset) > JUMP_WARN_MS) {
      logger.warn(`[CastSync] clock jump ${Math.round(best.offset - this.offset)} ms`);
    }
    this.exact = true;
    this.setOffset(best.offset);
  }

  private withTimeout<T>(p: Promise<T>): Promise<T> {
    let timer: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error("cast time timeout")), CLOCK_REQUEST_TIMEOUT_MS);
    });
    return Promise.race([p, timeout]).finally(() => clearTimeout(timer));
  }
}

/** Sync now, every [intervalMs], when the tab becomes visible and when the network comes back. */
export function startServerClock(clock: ServerClock, intervalMs = CLOCK_REFRESH_MS): () => void {
  const sync = () => void clock.sync();
  const onVisible = () => {
    if (document.visibilityState === "visible") sync();
  };
  sync();
  const timer = setInterval(sync, intervalMs);
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("online", sync);
  return () => {
    clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("online", sync);
  };
}

export const serverClock = new ServerClock(getCastServerTime);

let holders = 0;
let stopClock: (() => void) | null = null;

/** Keeps the singleton refreshing while at least one cast player is mounted. */
export function retainServerClock(): () => void {
  if (holders++ === 0) stopClock = startServerClock(serverClock);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (--holders === 0) {
      stopClock?.();
      stopClock = null;
    }
  };
}
